import { Alert, Box, Button, TableContainer, TablePagination } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { capitalize, compact, find, uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { Invoice, useInvoicesList } from '@/api/invoices';
import { useSubscriptionsInfo, useUpcomingInvoice } from '@/api/subscriptions';
import { usePurchasableItems } from '@/api/subscriptions/getPurchasableItems';
import { ErrorIcon, SuccessIcon } from '@/components/StatusIcons';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { InvoiceAmount } from '@/components/subscriptions/InvoiceAmount';
import { ACCOUNT_BALANCE_CREDIT_PRICE_LOOKUP_KEY, ONETIME_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';

import { Timestamp } from '../data/Timestamp';

export const BillingHistoryTable = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [[startingAfter, endingBefore], setStartingAfterEndingBefore] = useState<[string, string]>(['', '']);
    const { data: paidInvoices, isPending: isPaidInvoicesPending } = useInvoicesList({
        limit: rowsPerPage,
        startingAfter,
        endingBefore,
        status: 'paid',
    });
    const { data: pastDueInvoices } = useInvoicesList({
        limit: rowsPerPage,
        startingAfter,
        endingBefore,
        status: 'open',
    });
    const { data: subscriptionInfo } = useSubscriptionsInfo({
        status: 'latest',
    });
    const subscriptionCanceled = subscriptionInfo?.data?.[0]?.status === 'canceled';
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionInfo?.data?.[0]?.id,
        config: {
            enabled: !subscriptionCanceled,
        },
    });

    const handleChangePage = (event: unknown, newPage: number) => {
        if (!paidInvoices?.data) {
            return;
        }
        const isNextPage = newPage > page;
        const first = paidInvoices.data[0]?.id || '';
        const last = paidInvoices.data[paidInvoices.data.length - 1]?.id || '';
        const updated: [string, string] = isNextPage ? [last, ''] : ['', first];

        setPage(newPage);
        setStartingAfterEndingBefore(updated);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
        setStartingAfterEndingBefore(['', '']);
    };

    const count = (() => {
        // loading
        if (isPaidInvoicesPending) {
            return -1;
        }
        // account has no paid invoices yet
        if (!paidInvoices?.data?.length) {
            return 0;
        }
        // last page
        if (paidInvoices.data.length < rowsPerPage) {
            return rowsPerPage * page + paidInvoices.data.length;
        }
        // middle pages
        return -1;
    })();

    // when a subscription gets canceled, the overdue invoices remain open for some time and thus would be displayed to users requesting payment
    // users can still pay them but since the subscription is already canceled nothing happens.
    // this block hides the row but we should just mark those invoices as uncollectable
    // sc-247565
    const pastDueInvoice = useMemo(() => {
        const pastDueInvoice = pastDueInvoices?.data[0];
        const subscription = subscriptionInfo?.data.find(({ id }) => id === pastDueInvoice?.subscription);

        if (!subscription || subscription.status !== 'past_due') {
            return null;
        }

        return pastDueInvoice;
    }, [pastDueInvoices?.data, subscriptionInfo?.data]);

    if (paidInvoices?.data.length === 0) {
        return (
            <Alert severity="info" sx={{ alignItems: 'center', alignContent: 'center' }}>
                <Typography variant="body1">You have no invoices at this time.</Typography>
            </Alert>
        );
    }

    return (
        <Box data-testid="user-account-purchase-history-section">
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: grey[300] }}>
                            <TableCell sx={{ pl: 3 }}>Invoice number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Usage</TableCell>
                            <TableCell>Frequency</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Invoice date</TableCell>
                            <TableCell>Date paid</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pastDueInvoice && <BillingHistoryTableRow invoice={pastDueInvoice} />}
                        {!pastDueInvoice && upcomingInvoice?.data && !subscriptionCanceled && (
                            <UpcomingInvoiceTableRow invoice={upcomingInvoice.data} />
                        )}
                        {isPaidInvoicesPending &&
                            Array.from({ length: rowsPerPage }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell>{rowsPerPage * page + i + 1}</TableCell>
                                    <TableCell colSpan={8}>
                                        <GenericSkeleton lines={1} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        {paidInvoices?.data.map((row) => <BillingHistoryTableRow key={row.id} invoice={row} />)}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Invoices per page"
                slotProps={{ actions: { nextButton: { disabled: (paidInvoices?.data || []).length < rowsPerPage } } }}
            />
        </Box>
    );
};

const UpcomingInvoiceTableRow = ({ invoice }: { invoice: Invoice }) => {
    const nextPeriodStartEpoch = invoice?.subscription_period_start;
    return (
        <TableRow key={invoice.id} sx={{ '> *': { alignContent: 'baseline' } }}>
            <TableCell sx={{ pl: 3 }}>-</TableCell>
            <TableCell>Upcoming</TableCell>
            <TableCell>Telemetry subscription</TableCell>
            <TableCell>Monthly</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <InvoiceAmount invoice={invoice} noSourceTooltip={false} />
            </TableCell>
            <TableCell>
                <Timestamp unit="date" value={nextPeriodStartEpoch} type="medium" />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>-</TableCell>
            <TableCell align="right" sx={{ pr: 3 }}>
                <Button
                    variant="text"
                    size="small"
                    href="/payment"
                    sx={{
                        justifyContent: 'end',
                    }}
                >
                    Preview invoice
                </Button>
            </TableCell>
        </TableRow>
    );
};

const frequencyLabels: Record<Invoice['billing_frequency'], string> = {
    once: 'One-time',
    monthly: 'Monthly',
};

const BillingHistoryTableRow = ({ invoice }: { invoice: Invoice }) => {
    const { data: purchasableItems } = usePurchasableItems();
    const isInvoicePaid = invoice.status === 'paid';
    const isInvoiceCredited = invoice.total < 0;

    const usage = useMemo(
        () =>
            uniq(
                compact(
                    invoice.lines.map(({ price_id, subscription_item_id }) => {
                        if (subscription_item_id) {
                            return 'Telemetry subscription';
                        }
                        const priceLookupKey = find(purchasableItems, { price_id })?.price_lookup_key;
                        if (priceLookupKey === ONETIME_SATELLITE_CREDITS_PRICE_LOOKUP_KEY) {
                            return 'Satellite credits';
                        }
                        if (priceLookupKey === ACCOUNT_BALANCE_CREDIT_PRICE_LOOKUP_KEY) {
                            return 'Account balance fund';
                        }
                    })
                )
            ).join(', '),
        [invoice, purchasableItems]
    );

    const noSourceTooltip = usage === 'Account balance fund';

    const mixpanel = useMixpanel();
    const handleReceiptViewClick = () => {
        mixpanel?.track('Stripe receipt/invoice viewed', { status: invoice.status });
    };

    return (
        <TableRow key={invoice.id} sx={{ '> *': { alignContent: 'baseline' } }}>
            <TableCell sx={{ pl: 3 }}>{invoice.number}</TableCell>
            <TableCell>
                {isInvoicePaid ? (
                    <>
                        {isInvoiceCredited ? 'Credited' : capitalize(invoice.status)}
                        <SuccessIcon fontSize="small" sx={{ ml: 1, position: 'relative', top: 5 }} />
                    </>
                ) : (
                    <>
                        Overdue
                        <ErrorIcon fontSize="small" sx={{ ml: 1, position: 'relative', top: 5 }} />
                    </>
                )}
            </TableCell>
            <TableCell>{usage}</TableCell>
            <TableCell>{frequencyLabels[invoice.billing_frequency]}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <InvoiceAmount invoice={invoice} noSourceTooltip={noSourceTooltip} />
            </TableCell>
            <TableCell>
                <Timestamp variant="inherit" value={invoice.created} unit="date" type="medium" />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {invoice.status_transitions.paid_at && (
                    <Timestamp variant="inherit" value={invoice.status_transitions.paid_at} unit="date" type="medium" />
                )}
            </TableCell>
            <TableCell align="right" sx={{ pr: 3 }}>
                {invoice.hosted_invoice_url && (
                    <Button
                        variant="text"
                        size="small"
                        href={invoice.hosted_invoice_url}
                        onClick={handleReceiptViewClick}
                        sx={{
                            justifyContent: 'end',
                        }}
                    >
                        {isInvoicePaid ? 'View receipt' : 'Pay invoice'}
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
};
